import React, { useEffect, useMemo, useRef } from 'react';
import { translations } from '../translations';
import ChessyBrand from './ChessyBrand';

const SPLINE_VIEWER_SRC = 'https://unpkg.com/@splinetool/viewer@1.9.82/build/spline-viewer.js';
const SPLINE_SCENE_URL = 'https://prod.spline.design/kZDDjO5HuC9GJUM2/scene.splinecode';

function Hero({ language, onOpenDrawer, onOpenAcademy, emphasizeContinuePathCta = false }) {
  const t = translations[language];
  const primaryLabel = emphasizeContinuePathCta
    ? (language === 'ro' ? 'Continuă traseul tău de învățare' : 'Continue your learning path')
    : t.btnPrimary;
  const cardRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  const robotCardCopy = useMemo(() => (
    language === 'ro'
      ? {
          name: 'Chessy AI',
          subtitle: 'Antrenorul tău de șah AI'
        }
      : {
          name: 'Chessy AI',
          subtitle: 'Your AI chess coach'
        }
  ), [language]);

  const openChessyPlayground = () => {
    if (typeof onOpenAcademy === 'function') {
      onOpenAcademy('playground');
      return;
    }
    window.location.hash = '#/playground';
  };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.customElements?.get('spline-viewer')) return undefined;

    const existing = document.querySelector(`script[data-spline-viewer="true"]`);
    if (existing) return undefined;

    const script = document.createElement('script');
    script.type = 'module';
    script.src = SPLINE_VIEWER_SRC;
    script.setAttribute('data-spline-viewer', 'true');
    document.head.appendChild(script);
    return undefined;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const hideSplineBrand = (viewer) => {
      try {
        const sr = viewer?.shadowRoot;
        if (!sr) return;
        const style = document.createElement('style');
        style.textContent = [
          '#logo{display:none!important}',
          'a[href*="spline.design"]{display:none!important}',
          '[class*="logo"]{display:none!important}',
          '[id*="watermark"]{display:none!important}'
        ].join('');
        sr.appendChild(style);
      } catch {
        // noop
      }
    };

    const viewers = Array.from(
      cardRef.current?.querySelectorAll('spline-viewer') || []
    );
    if (!viewers.length) return undefined;

    const listeners = viewers.map((viewer) => {
      const handler = () => hideSplineBrand(viewer);
      viewer.addEventListener('load', handler);
      window.setTimeout(handler, 1200);
      window.setTimeout(handler, 3200);
      return { viewer, handler };
    });

    return () => {
      listeners.forEach(({ viewer, handler }) => {
        viewer.removeEventListener('load', handler);
      });
    };
  }, []);

  useEffect(() => {
    const card = cardRef.current;
    const canvas = canvasRef.current;
    if (!card || !canvas) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let tx = card.offsetWidth / 2;
    let ty = card.offsetHeight / 2;
    let cx = tx;
    let cy = ty;

    const draw = () => {
      cx += (tx - cx) * 0.1;
      cy += (ty - cy) * 0.1;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const radius = Math.max(canvas.width, canvas.height) * 0.65;
      const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
      gradient.addColorStop(0, 'rgba(80,100,240,0.18)');
      gradient.addColorStop(0.4, 'rgba(80,100,240,0.05)');
      gradient.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      rafRef.current = window.requestAnimationFrame(draw);
    };

    const resize = () => {
      canvas.width = card.offsetWidth;
      canvas.height = card.offsetHeight;
    };
    resize();

    const onMouseMove = (e) => {
      const r = card.getBoundingClientRect();
      tx = e.clientX - r.left;
      ty = e.clientY - r.top;
    };
    const onEnter = () => {
      if (!rafRef.current) draw();
    };
    const onLeave = () => {
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(resize);
      ro.observe(card);
    }

    card.addEventListener('mousemove', onMouseMove);
    card.addEventListener('mouseenter', onEnter);
    card.addEventListener('mouseleave', onLeave);

    return () => {
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
      if (ro) ro.disconnect();
      card.removeEventListener('mousemove', onMouseMove);
      card.removeEventListener('mouseenter', onEnter);
      card.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return (
    <section className="hero" style={{ width: '100%' }}>
      <div className="hero-left">
        <span className="hero-eyebrow">{t.eyebrow}</span>

        <h1 className="hero-headline">
          {t.heroHeadline[0]}<br />
          {t.heroHeadline[1]}<br />
          <em>
            {t.heroHeadline[2]}<br />
            {t.heroHeadline[3]}
          </em>
        </h1>

        <p className="hero-sub">{t.heroSub}</p>

        <div className="hero-actions">
          <button 
            className={`btn-primary ${emphasizeContinuePathCta ? 'btn-primary-continue btn-primary-bounce' : ''}`.trim()}
            onClick={() => {
              if (typeof onOpenAcademy === 'function') {
                onOpenAcademy('academy');
                return;
              }
              const features = document.querySelector('.features');
              if (features) {
                window.scrollTo({ top: features.offsetTop, behavior: 'smooth' });
              }
            }}
          >
            {primaryLabel}
          </button>
          <button className="btn-about" onClick={onOpenDrawer}>
            <span>{t.aboutLabel}</span> ↗
          </button>
        </div>
      </div>

      <div className="hero-right">
        <div className="video-card">
          <video autoPlay loop muted playsInline>
            <source
              src="https://res.cloudinary.com/dsed1kupc/video/upload/v1770760610/presentation_kg2fgk.mp4"
              type="video/mp4"
            />
          </video>
        </div>
        <p className="video-tagline">
          {t.videoTagline[0]}
          <span>{t.videoTagline[1]}</span>
        </p>

        <article
          className="hero-chessy-card"
          ref={cardRef}
          role="button"
          tabIndex={0}
          aria-label={language === 'ro' ? 'Deschide Chessy AI Playground' : 'Open Chessy AI Playground'}
          onClick={openChessyPlayground}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openChessyPlayground();
            }
          }}
        >
          <canvas ref={canvasRef} className="hero-chessy-spotlight" />
          <div className="hero-chessy-label-card">
            <h3 className="hero-chessy-name">
              <ChessyBrand />
            </h3>
            <p className="hero-chessy-sub">{robotCardCopy.subtitle}</p>
          </div>

          <div className="hero-chessy-right">
            <div className="hero-chessy-spline-wrap">
              <spline-viewer url={SPLINE_SCENE_URL} />
            </div>
            <div className="hero-chessy-mask-t" />
            <div className="hero-chessy-mask-b" />
            <div className="hero-chessy-mask-r" />
          </div>
        </article>
      </div>
    </section>
  );
}

export default Hero;
